// Identity stores — phone number (citizen) + ADMIN/AGENCY session flags.
//
// Until Feb 2026, this module stored opaque admin/agency UUID tokens in
// localStorage. The new transport (see /app/backend/cookie_auth.py)
// puts those tokens in HTTPONLY cookies that JavaScript cannot touch,
// so we keep ONLY a lightweight "is signed in" flag (and agency
// profile metadata for UI rendering) on the client side.
//
// Logout still wipes the local flag in addition to the server-side
// session-row delete — the flag is the auth-gate single source of
// truth on the React side.

const KEY_PHONE = "rr_user_phone";
const KEY_CITIZEN_TOKEN = "rr_citizen_token"; // signed device token, see citizen_auth.py
const KEY_ADMIN_FLAG = "rr_admin_session";   // "1" | absent
const KEY_AGENCY_INFO = "rr_agency_info";    // JSON: { id, email, name, emergency_type }

const phoneListeners = new Set();
const adminListeners = new Set();
const agencyListeners = new Set();

// Sep 2026 bugfix — grandfathered users (phone saved, no device-token
// yet) get one silently re-onboarded by OnboardingGate on boot. That
// call is async, so "my own data" fetches (contacts, medical profile)
// firing on the SAME boot could lose the race and 401 before the
// token lands. `citizenHealPromise` lets any component await the
// SAME in-flight re-onboard call instead of firing its own — it does
// NOT block rendering (the SOS flow never depends on it).
let citizenHealPromise = null;

const readPhone = () => { try { return localStorage.getItem(KEY_PHONE) || ""; } catch (_e) { return ""; } };
const readToken = () => { try { return localStorage.getItem(KEY_CITIZEN_TOKEN) || ""; } catch (_e) { return ""; } };
const readAdminFlag = () => { try { return localStorage.getItem(KEY_ADMIN_FLAG) === "1"; } catch (_e) { return false; } };
const readAgency = () => {
  try {
    const raw = localStorage.getItem(KEY_AGENCY_INFO);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj && obj.id ? obj : null;
  } catch (_e) { return null; }
};

let phoneCache = readPhone();
let tokenCache = readToken();
let adminCache = readAdminFlag();
let agencyCache = readAgency();

export const userIdentity = {
  getPhone() { return phoneCache; },
  setPhone(v) {
    phoneCache = v || "";
    try {
      if (phoneCache) localStorage.setItem(KEY_PHONE, phoneCache);
      else localStorage.removeItem(KEY_PHONE);
    } catch (e) { /* noop */ void e; }
    phoneListeners.forEach((fn) => { try { fn(phoneCache); } catch (e) { /* noop */ void e; } });
  },
  getToken() { return tokenCache; },
  setToken(v) {
    tokenCache = v || "";
    try {
      if (tokenCache) localStorage.setItem(KEY_CITIZEN_TOKEN, tokenCache);
      else localStorage.removeItem(KEY_CITIZEN_TOKEN);
    } catch (e) { /* noop */ void e; }
  },
  subscribe(fn) {
    phoneListeners.add(fn);
    return () => phoneListeners.delete(fn);
  },
  // Called by OnboardingGate with the in-flight re-onboard promise so
  // other components can wait on the SAME call (see comment above).
  setHealPromise(p) { citizenHealPromise = p; },
  // Resolves once a device-token is available, or immediately if none
  // is needed / no heal is in flight. Never rejects, never hangs —
  // safe to `await` before a "my own data" fetch.
  async ready() {
    if (tokenCache) return tokenCache;
    if (citizenHealPromise) {
      try { await citizenHealPromise; } catch (_e) { /* noop */ }
    }
    return tokenCache;
  },
};

// adminAuth — cookie-based. The actual token lives in an httpOnly
// cookie set by /api/admin/login; here we only track the boolean
// "logged in" flag so the AuthGate can route without hitting the API
// on every render.
export const adminAuth = {
  isLoggedIn() { return adminCache; },
  markLoggedIn() {
    adminCache = true;
    try { localStorage.setItem(KEY_ADMIN_FLAG, "1"); } catch (e) { /* noop */ void e; }
    adminListeners.forEach((fn) => { try { fn(true); } catch (e) { /* noop */ void e; } });
  },
  clear() {
    adminCache = false;
    try { localStorage.removeItem(KEY_ADMIN_FLAG); } catch (e) { /* noop */ void e; }
    adminListeners.forEach((fn) => { try { fn(false); } catch (e) { /* noop */ void e; } });
  },
  subscribe(fn) {
    adminListeners.add(fn);
    return () => adminListeners.delete(fn);
  },
};

// agencyAuth — cookie-based. Only the agency profile (id, email,
// name, emergency_type) is kept locally for UI rendering. The session
// token lives in the rr_agency_token httpOnly cookie.
export const agencyAuth = {
  get() { return agencyCache; },
  isLoggedIn() { return !!agencyCache; },
  set(agencyInfo) {
    agencyCache = (agencyInfo && agencyInfo.id) ? agencyInfo : null;
    try {
      if (agencyCache) localStorage.setItem(KEY_AGENCY_INFO, JSON.stringify(agencyCache));
      else localStorage.removeItem(KEY_AGENCY_INFO);
    } catch (e) { /* noop */ void e; }
    agencyListeners.forEach((fn) => { try { fn(agencyCache); } catch (e) { /* noop */ void e; } });
  },
  clear() { this.set(null); },
  subscribe(fn) {
    agencyListeners.add(fn);
    return () => agencyListeners.delete(fn);
  },
};

// One-time migration: if a legacy token blob exists in localStorage,
// clear it. The httpOnly cookie may or may not be present yet; the
// AuthGate will probe /admin/me + /agency/me on mount to (re)establish
// the session flag.
try {
  if (localStorage.getItem("rr_admin_token")) {
    localStorage.removeItem("rr_admin_token");
    adminCache = true; // assume an old token used to work; AuthGate will verify
    localStorage.setItem(KEY_ADMIN_FLAG, "1");
  }
  const legacyAgency = localStorage.getItem("rr_agency_session");
  if (legacyAgency) {
    try {
      const parsed = JSON.parse(legacyAgency);
      if (parsed && parsed.agency && parsed.agency.id) {
        localStorage.setItem(KEY_AGENCY_INFO, JSON.stringify(parsed.agency));
        agencyCache = parsed.agency;
      }
    } catch (_e) { /* noop */ }
    localStorage.removeItem("rr_agency_session");
  }
} catch (e) { void e; }
